// 9fbef606107a605d69c0edbcd8029e5d
import React from 'react';
import PropTypes from 'prop-types';
import withStyles from '@tcp/core/src/components/common/hoc/withStyles';
import { isMobileWeb } from '@tcp/core/src/utils';
import { hideProductOriginalPrice } from '@tcp/core/src/components/features/browse/ProductListing/molecules/ProductList/views/ProductGridItem.util';
import RatingAndReviews from '@tcp/core/src/components/features/browse/BundleProduct/atoms/RatingsAndReviews';
import { getProductListToPath } from '@tcp/core/src/components/features/browse/ProductListing/molecules/ProductList/utils/productsCommonUtils';
import {
  IMG_DATA_PLP,
  VID_DATA_SLP,
} from '@tcp/core/src/components/features/browse/ProductListing/molecules/ProductList/config';
import Promotions from '@tcp/core/src/components/features/browse/BundleProduct/atoms/Promotions';
import {
  getIconPath,
  getVideoUrl,
  getDynamicBadgeQty,
  getStringAcronym,
} from '@tcp/core/src/utils/utils';
import ImageComponent from '@tcp/core/src/components/features/browse/BundleProduct/atoms/Image';
import Image from '@tcp/core/src/components/common/atoms/Image';
import Price from '@tcp/core/src/components/features/browse/BundleProduct/atoms/Price';
import Anchor from '@tcp/core/src/components/common/atoms/Anchor';
import Constants from '@tcp/core/src/components/common/molecules/Recommendations/container/Recommendations.constants';
import BodyCopy from '@tcp/core/src/components/common/atoms/BodyCopy';
import styles, {
  ImageContainer,
  PriceSection,
  ATBIcon,
  Title,
} from '../styles/RecommendationTile.style';

const renderImage = ({
  imageUrl,
  dynamicBadgeOverlayQty,
  pdpAsPath,
  pdpToPath,
  name,
  tcpStyleType,
  primaryBrand,
}) => {
  const isVideoUrl = imageUrl && getVideoUrl(imageUrl);
  const mainConfig = isVideoUrl ? VID_DATA_SLP.imgConfig : IMG_DATA_PLP.imgConfig;

  return (
    <ImageContainer className="recommendation-tile-image">
      <ImageComponent
        pdpAsPath={pdpAsPath}
        pdpToPath={pdpToPath}
        dynamicBadgeOverlayQty={dynamicBadgeOverlayQty}
        mainConfig={mainConfig}
        isVideoUrl={isVideoUrl}
        imageData={{ alt: name, url: imageUrl }}
        TCPStyleType={tcpStyleType}
        badgeConfig={IMG_DATA_PLP.badgeConfig}
        primaryBrand={primaryBrand}
        lazyLoadImage
      />
    </ImageContainer>
  );
};

const renderPrice = ({
  currencySymbol,
  currencyAttributes,
  listPrice,
  offerPrice,
  merchantTag,
  isHideOriginalPrice,
}) => {
  return (
    <Price
      minListPrice={listPrice}
      minOfferPrice={offerPrice}
      merchantTag={merchantTag}
      isHideOriginalPrice={isHideOriginalPrice}
      currencyAttributes={currencyAttributes}
      currencySymbol={currencySymbol}
    />
  );
};

const renderATBIcon = ({
  name,
  handleClick,
  productId,
  isBagPage,
  bagItemCount,
  isAddedToBag,
  orderItemsCount,
  spcBag,
  headerLabel,
}) => {
  return (
    <ATBIcon
      onClick={() => handleClick(productId, headerLabel)}
      aria-label={`Add to bag ${name}`}
      isBagPage={isBagPage}
      bagItemCount={bagItemCount}
      isAddedToBag={isAddedToBag}
      orderItemsCount={orderItemsCount}
      spcBag={spcBag}
      className="recommendation-tile-atc-icon"
    >
      <Image
        className="atb-icon-image"
        src={getIconPath('add-to-bag-icon-blue')}
        aria-hidden={true}
        alt="Add to bag Icon"
      />
    </ATBIcon>
  );
};

export const renderPriceSection = ({
  currencySymbol,
  currencyAttributes,
  listPrice,
  offerPrice,
  merchantTag,
  isHideOriginalPrice,
  name,
  handleClick,
  productId,
  isBagPage,
  bagItemCount,
  isAddedToBag,
  orderItemsCount,
  spcBag,
}) => {
  return (
    <PriceSection>
      {renderPrice({
        currencySymbol,
        currencyAttributes,
        listPrice,
        offerPrice,
        merchantTag,
        isHideOriginalPrice,
      })}
      {renderATBIcon({
        name,
        handleClick,
        productId,
        isBagPage,
        bagItemCount,
        isAddedToBag,
        orderItemsCount,
        spcBag,
      })}
    </PriceSection>
  );
};

const renderTitle = ({ pdpToPath, pdpAsPath, name }) => {
  return (
    <Anchor to={pdpToPath} asPath={pdpAsPath}>
      <Title fontFamily="secondary" fontSize="fs12" className="product-name">
        {name}
      </Title>
    </Anchor>
  );
};

const renderPromotions = ({
  promotionsData,
  TCPPromotionPrice,
  TCPPromotionID,
  isUpdatedPromotionsDisplayEnabled,
  serverTime,
}) => {
  return (
    <Promotions
      promotionsData={promotionsData}
      TCPPromotionPrice={TCPPromotionPrice}
      TCPPromotionID={TCPPromotionID}
      isUpdatedPromotionsDisplayEnabled={isUpdatedPromotionsDisplayEnabled}
      serverTime={serverTime}
      isFormRecommendationTile
    />
  );
};

export const renderRatings = ({ ratings, reviewsCount }) => {
  return <RatingAndReviews ratings={ratings} reviewsCount={reviewsCount} />;
};

const renderLastPurchasedSize = ({ lastPurchasedSize }) => {
  return (
    <BodyCopy
      component="div"
      fontSize={isMobileWeb() ? 'fs10' : 'fs11'}
      fontWeight="normal"
      fontFamily="secondary"
      color="gray.800"
    >{`Last Purchased ${lastPurchasedSize}`}</BodyCopy>
  );
};

function extractBasicImageUrl(obj) {
  const foundEntry = Object?.entries(obj)?.find(([, value]) =>
    Object?.prototype?.hasOwnProperty?.call(value, 'basicImageUrl')
  );

  if (foundEntry) {
    return foundEntry?.[1]?.basicImageUrl;
  }
  return null;
}

const RecommendationTile = ({
  className,
  product,
  isHideOriginalPriceAndMarchantTagEnabled,
  isDynamicBadgeEnabled,
  headerLabel,
  page,
  currencySymbol,
  currencyAttributes,
  onQuickViewOpenClick,
  isBagPage,
  bagItemCount,
  isAddedToBag,
  orderItemsCount,
  spcBag,
  fromPageBranch,
  isBackToQuickviewCTAEnabled,
  isQvModalOpen,
  closeQvModal,
  promotionsData = {},
  serverTime,
  isPromoMsgEnabledRecommendation,
  sendEventsToMonetateActn,
}) => {
  const {
    recToken,
    productInfo,
    colorsMap,
    imagesByColor,
    generalProductId,
    lastPurchasedSize,
    monRecsCarouselId,
  } = product;
  const extractedBasicImageUrl = extractBasicImageUrl(imagesByColor);

  const { basicImageUrl: imageUrlValue } = imagesByColor.SPRUCESHAD || '';
  const imageUrl = imageUrlValue || extractedBasicImageUrl;
  const { miscInfo } = colorsMap[0];
  const { badge3 } = miscInfo;
  const {
    pdpUrl,
    ratings,
    reviewsCount,
    tcpStyleQty,
    tcpStyleType,
    primaryBrand,
    hardMarkDown,
    name,
    listPrice,
    offerPrice,
    TCPPromotionID,
    TCPPromotionPrice,
  } = productInfo;

  const isHideOriginalPrice = hideProductOriginalPrice(
    hardMarkDown,
    isHideOriginalPriceAndMarchantTagEnabled
  );
  const isUpdatedPromotionsDisplayEnabled = true;

  const dynamicBadgeOverlayQty = getDynamicBadgeQty(
    tcpStyleType,
    tcpStyleQty,
    isDynamicBadgeEnabled
  );

  const pdpToPath = getProductListToPath(pdpUrl);
  let productCrossSellDriver = '';
  if (monRecsCarouselId) {
    const carouselNameAcrynm = getStringAcronym(headerLabel);
    productCrossSellDriver = `${monRecsCarouselId}-${carouselNameAcrynm}`;
  }

  const newPdpToPath =
    pdpToPath &&
    `${pdpToPath}&viaModule=${
      Constants.RECOMMENDATION
    }&viaPage=${page}&dataSource=recommendation&recommendationCarousel=${
      headerLabel || ''
    }&recToken=${recToken}&productCrossSellDriver=${productCrossSellDriver}
    `;

  const handleClick = (colorProductId) => {
    if (isBackToQuickviewCTAEnabled && isAddedToBag && isQvModalOpen && closeQvModal) {
      closeQvModal();
    }
    if (recToken && sendEventsToMonetateActn) {
      sendEventsToMonetateActn({ recToken: [recToken], isRecsClick: true });
    }
    requestAnimationFrame(() => {
      onQuickViewOpenClick({
        colorProductId,
        fromPage: page,
        fromPageBranch,
        ...(productCrossSellDriver && { productCrossSellDriver }),
      });
    });
  };

  return (
    <div className={`${className} recommendation-tile`}>
      {renderImage({
        imageUrl,
        dynamicBadgeOverlayQty,
        pdpAsPath: pdpUrl,
        pdpToPath: newPdpToPath,
        name,
        tcpStyleType,
        primaryBrand,
      })}
      {renderPriceSection({
        currencySymbol,
        currencyAttributes,
        listPrice,
        offerPrice,
        merchantTag: badge3,
        isHideOriginalPrice,
        name,
        handleClick,
        productId: generalProductId || productInfo?.generalProductId,
        isBagPage,
        bagItemCount,
        isAddedToBag,
        orderItemsCount,
        spcBag,
      })}

      {renderTitle({ pdpToPath: newPdpToPath, pdpAsPath: pdpUrl, name })}
      {renderRatings({ ratings, reviewsCount })}
      {lastPurchasedSize && renderLastPurchasedSize({ lastPurchasedSize })}
      {isPromoMsgEnabledRecommendation &&
        renderPromotions({
          promotionsData,
          TCPPromotionPrice,
          TCPPromotionID,
          isUpdatedPromotionsDisplayEnabled,
          serverTime,
        })}
    </div>
  );
};

RecommendationTile.propTypes = {
  className: PropTypes.string,
  product: PropTypes.shape({}),
  isHideOriginalPriceAndMarchantTagEnabled: PropTypes.bool,
  isDynamicBadgeEnabled: PropTypes.shape({}),
  headerLabel: PropTypes.string,
  page: PropTypes.string,
  currencySymbol: PropTypes.string,
  currencyAttributes: PropTypes.shape({}),
  onQuickViewOpenClick: PropTypes.func.isRequired,
};
RecommendationTile.defaultProps = {
  className: '',
  product: {},
  isHideOriginalPriceAndMarchantTagEnabled: false,
  isDynamicBadgeEnabled: {},
  headerLabel: '',
  page: '',
  currencySymbol: '',
  currencyAttributes: {},
};

export default withStyles(RecommendationTile, styles);

export { RecommendationTile as RecommendationTileVanilla };
